/* ==========================================================================
   AURA — COLLECTION CONTROLLER
   --------------------------------------------------------------------------
   PRINCIPLE: the UI must never know whether the page reloads or not.

   The UI (dropdown, filter options, chips, reset) emits INTENT only. The
   controller translates intent into a URLSearchParams "state" and hands it
   to a TRANSPORT.

   Phase 2 is now live: AjaxTransport fetches the collection URL, swaps the
   grid + filter UI in place, and pushes history — no page reload. If the
   fetch fails it falls back to a hard navigation (FormSubmitTransport
   behaviour) so filtering never breaks.
   ========================================================================== */

/* -------- Transports (interchangeable; identical interface) -------------- */

class BaseTransport {
  apply(_params) {
    throw new Error('Transport.apply(params) must be implemented by a subclass.');
  }
}

class FormSubmitTransport extends BaseTransport {
  constructor(form) {
    super();
    this.form = form;
  }
  apply(params) {
    const action = this.form.getAttribute('action') || window.location.pathname;
    window.location.assign(`${action}?${params.toString()}`);
  }
}

class AjaxTransport extends BaseTransport {
  constructor(form, onUpdate) {
    super();
    this.form = form;
    this.onUpdate = onUpdate;
    this.action = form.getAttribute('action') || window.location.pathname;
  }

  apply(params) {
    params.delete('page');
    auraKeepView(params);
    const url = `${this.action}?${params.toString()}`;

    document.body.classList.add('is-filtering');

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.swap(doc);
        history.pushState({ aura: true }, '', url);
        /* AJAX succeeded — overlay never closed, so drop the reopen flag. */
        sessionStorage.removeItem('aura-filter-open');
        if (this.onUpdate) this.onUpdate();
      })
      .catch(() => {
        /* Fallback: hard navigation. The reopen flag (set by the controller
           before apply) restores the overlay after the reload. */
        window.location.assign(url);
      })
      .finally(() => {
        document.body.classList.remove('is-filtering');
      });
  }

  swap(doc) {
    /* Product grid */
    this.swapInner(doc, '#AuraProductGrid');

    /* Load More — a filter/sort change resets to a fresh first page, so the
       button must pick up the new filtered pagination (a new next-page URL,
       or disappear if everything now fits on one page). Never left stale. */
    this.swapLoadMore(doc);

    /* Filter options — update IN PLACE inside each accordion so the accordion
       wrappers (and therefore their open/closed state + JS bindings) survive
       untouched. Only the option list / slider inside each group is replaced. */
    this.swapFilterOptions(doc);

    /* Section count in the overlay title */
    this.swapInnerAll(doc, '.aura-overlay--filters .aura-overlay__title');

    /* Filter-bar trigger count ("Filters (N)") on both static + floating bars */
    this.swapInnerAll(doc, '.aura-filter-bar__filters span');

    /* Sort dropdown label + hidden sort input */
    this.swapInnerAll(doc, '.aura-dropdown__label');
    const incomingSort = doc.querySelector('#AuraSortInput');
    const currentSort = document.querySelector('#AuraSortInput');
    if (incomingSort && currentSort) currentSort.value = incomingSort.value;

    /* Sort option ROWS — without this the label moved to the newly chosen sort
       but the checkmark stayed on the previous row. Inner-only swap keeps the
       .aura-dropdown__menu element itself (and its open/closed state, plus
       AuraDropdown's cached reference to it) intact. Rows come back unbound,
       so afterUpdate() re-runs bindSort(). */
    this.swapInnerAll(doc, '.aura-dropdown__menu');
    this.swapInnerAll(doc, '.aura-overlay--sort .aura-overlay__body');
  }

  /* Replace only the option list / slider inside each accordion (overlay +
     sidebar), leaving the wrapper, trigger, open state, and height intact.

     Matching is done by POSITION within each container — never by global id.
     Shopify gives the price_range filter a blank param_name, so the overlay
     and sidebar price groups can collide on id; a global getElementById would
     then pull the sidebar's old "From/To" price-facet into the overlay. The
     filter set + order is stable across requests, so index matching is safe. */
  swapFilterOptions(doc) {
    this.swapGroupInners(doc, '.aura-overlay--filters .aura-accordion__content');
    this.swapGroupInners(doc, '.aura-filter-sidebar .aura-accordion__content');
  }

  swapGroupInners(doc, selector) {
    const current = document.querySelectorAll(selector);
    const incoming = doc.querySelectorAll(selector);

    current.forEach((content, i) => {
      const inc = incoming[i];
      if (!inc) return;

      const curInner = content.querySelector('.aura-accordion__inner');
      const incInner = inc.querySelector('.aura-accordion__inner');
      if (curInner && incInner) curInner.innerHTML = incInner.innerHTML;

      /* If this group is open with a measured pixel height, let it grow to
         fit the new contents instead of clipping. */
      if (content.style.height && content.style.height !== 'auto') {
        content.style.height = 'auto';
      }
    });
  }

  swapInner(doc, selector) {
    const incoming = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  }

  /* Unlike swapInner, presence/absence is meaningful in BOTH directions here.

     Absent incoming = the new (filtered) result set fits on one page, so the
     current button must be cleared, not left pointing at a stale next URL.

     Absent current = the page we are on had no next page (e.g. a narrow
     "Rayhaan" filter), so Liquid never rendered the #AuraLoadMore wrapper at
     all. Widening the filter back out then has nothing to swap into, and
     "Naloži več" silently stays gone even though pages 2+ now exist — so the
     wrapper is created and inserted after the grid, exactly where the section
     renders it. */
  swapLoadMore(doc) {
    const incoming = doc.querySelector('#AuraLoadMore');
    const current = document.querySelector('#AuraLoadMore');

    if (current) {
      current.innerHTML = incoming ? incoming.innerHTML : '';
      return;
    }

    if (!incoming) return;
    const grid = document.querySelector('#AuraProductGrid');
    if (!grid) return;
    grid.insertAdjacentElement('afterend', document.importNode(incoming, true));
  }

  swapInnerAll(doc, selector) {
    const incoming = doc.querySelector(selector);
    if (!incoming) return;
    document.querySelectorAll(selector).forEach(el => {
      el.innerHTML = incoming.innerHTML;
    });
  }
}

/* -------- Alternate-template persistence --------------------------------- */

/* A collection reached through an alternate template via ?view= (e.g.
   /collections/lattafa?view=testerji) must keep that param on every URL the
   controller rebuilds. Shopify's filter URLs and a fresh reset don't carry
   it, and losing it silently swaps the page back to the default template. */
function auraKeepView(params) {
  const view = new URLSearchParams(window.location.search).get('view');
  if (view && !params.has('view')) params.set('view', view);
  return params;
}

/* -------- Money parsing (for the price-sort correction below) ------------- */

/* Money text is merchant- and locale-formatted ("64,90 €", "1.064,90 €",
   "$1,064.90"), so the separators can't be assumed. Treat the LAST separator as
   the decimal point and drop the rest — that reads 1.064,90 and 1,064.90
   identically without knowing the locale. A trailing group of exactly three
   digits is a thousands separator, not decimals ("1.064" is 1064, not 1.064). */
function auraParseMoney(text) {
  const digits = (text || '').replace(/[^\d.,]/g, '');
  if (!digits) return Number.POSITIVE_INFINITY;

  const lastSep = Math.max(digits.lastIndexOf(','), digits.lastIndexOf('.'));
  let intPart = digits;
  let fracPart = '';

  if (lastSep !== -1) {
    const tail = digits.slice(lastSep + 1);
    if (tail.length !== 3) {
      intPart = digits.slice(0, lastSep);
      fracPart = tail;
    }
  }

  const value = parseFloat(intPart.replace(/[.,]/g, '') + (fracPart ? '.' + fracPart : ''));
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

/* The price a shopper actually sees on the card: the sale price when there is
   one, otherwise the regular price. Cards with no price sink to the end. */
function auraCardPrice(item) {
  const el = item.querySelector('.aura-card__price-sale, .aura-card__price-regular');
  return el ? auraParseMoney(el.textContent) : Number.POSITIVE_INFINITY;
}

/* -------- Controller ----------------------------------------------------- */

class AuraCollectionController {
  constructor(form) {
    this.form = form;
    this.transport = new AjaxTransport(form, () => this.afterUpdate());
    this.sectionId = form.dataset.sectionId || 'aura-collection-grid';
    this.sortInput = form.querySelector('#AuraSortInput');
    this.chips = new AuraActiveChips(this);
    this.searchTerm = '';

    this.bindSort();
    this.bindResets();
    this.bindFilterOptions();
    this.initSliders();
    this.bindLoadMore();
    this.bindCollectionSearch();
    this.enforcePriceOrder();

    /* Reflect the real URL when the user navigates back/forward. */
    window.addEventListener('popstate', () => window.location.reload());
  }

  currentParams() {
    return new URLSearchParams(window.location.search);
  }

  /* Called after every successful AJAX swap to re-wire the fresh DOM. */
  afterUpdate() {
    this.sortInput = this.form.querySelector('#AuraSortInput');
    this.bindSort();
    this.bindResets();
    this.bindFilterOptions();
    this.initSliders();
    this.bindLoadMore();
    this.bindCollectionSearch();
    this.chips.render();
    this.enforcePriceOrder();
  }

  applySort(value) {
    const params = this.currentParams();
    params.set('sort_by', value);
    params.delete('page');
    sessionStorage.setItem('aura-filter-open', '1');
    this.transport.apply(params);
  }

  applyFilterToggle(option) {
    const urlAdd = option.dataset.urlAdd;
    const urlRemove = option.dataset.urlRemove;
    const isSelected = option.classList.contains('is-selected');
    const target = isSelected ? urlRemove : urlAdd;

    /* Optimistic UI: toggle immediately so the tap feels instant. The AJAX
       swap then replaces the node with Shopify's authoritative state. */
    option.classList.toggle('is-selected', !isSelected);
    this.chips.render();

    /* Reopen flag is only consumed by the hard-navigation fallback. */
    sessionStorage.setItem('aura-filter-open', '1');

    if (target) {
      const url = new URL(target, window.location.origin);
      this.transport.apply(url.searchParams);
      return;
    }

    const param = option.dataset.param;
    const value = option.dataset.value;
    if (!param) return;
    const params = this.currentParams();
    const existing = params.getAll(param);
    params.delete(param);
    if (isSelected) {
      existing.filter(v => v !== value).forEach(v => params.append(param, v));
    } else {
      existing.forEach(v => params.append(param, v));
      params.append(param, value);
    }
    params.delete('page');
    this.transport.apply(params);
  }

  /* Remove one active filter from a chip click — same AJAX path as deselecting
     it in the accordion (no hard refresh). */
  removeFilter(item) {
    if (!item || !item.urlRemove) return;

    /* Optimistic UI: deselect every matching option immediately. */
    document.querySelectorAll('.aura-filter-option.is-selected').forEach(opt => {
      if (opt.dataset.urlRemove === item.urlRemove) {
        opt.classList.remove('is-selected');
      }
    });
    this.chips.render();

    sessionStorage.setItem('aura-filter-open', '1');
    const url = new URL(item.urlRemove, window.location.origin);
    this.transport.apply(url.searchParams);
  }

  applyReset() {
    const params = new URLSearchParams();
    if (this.sortInput && this.sortInput.value) {
      params.set('sort_by', this.sortInput.value);
    }
    sessionStorage.setItem('aura-filter-open', '1');
    this.transport.apply(params);
  }

  bindSort() {
    document.querySelectorAll('.aura-list-row[data-value], .aura-overlay--sort .aura-filter-option[data-value]')
      .forEach(row => {
        if (row.dataset.auraBound) return;
        row.dataset.auraBound = '1';
        row.addEventListener('click', e => {
          e.preventDefault();
          this.applySort(row.dataset.value);
        });
      });
  }

  bindFilterOptions() {
    document.querySelectorAll('.aura-overlay--filters .aura-filter-option, .aura-filter-sidebar .aura-filter-option')
      .forEach(opt => {
        if (opt.dataset.auraBound) return;
        opt.dataset.auraBound = '1';
        opt.addEventListener('click', e => {
          e.preventDefault();
          this.applyFilterToggle(opt);
        });
      });
  }

  bindResets() {
    document.querySelectorAll('.aura-filter-footer__reset, [data-aura-reset]')
      .forEach(btn => {
        if (btn.dataset.auraBound) return;
        btn.dataset.auraBound = '1';
        btn.addEventListener('click', e => {
          e.preventDefault();
          this.applyReset();
        });
      });
  }

  initSliders() {
    document.querySelectorAll('[data-price-slider]').forEach(el => {
      if (el.dataset.sliderReady) return;
      el.dataset.sliderReady = '1';
      new AuraPriceSlider(el, this);
    });
  }

  /* Section-rendered URL for a pagination link. Uses this section's real ID:
     a JSON-template section requested by filename renders with DEFAULT
     settings (per Shopify's Section Rendering docs), which dropped
     per-template settings — tester mode, gender priority — from page 2. */
  sectionPageUrl(url) {
    const next = new URL(url, window.location.origin);
    auraKeepView(next.searchParams);
    next.searchParams.set('section_id', this.sectionId);
    return next.pathname + next.search;
  }

  bindLoadMore() {
    document.querySelectorAll('[data-load-more]').forEach(btn => {
      if (btn.dataset.auraBound) return;
      btn.dataset.auraBound = '1';
      btn.addEventListener('click', () => this.loadMore(btn));
    });
  }

  /* Appends the next page onto the grid instead of replacing it — the
     opposite of a filter/sort change, which resets to a fresh first page
     via AjaxTransport.swap(). No history entry: revealing more of the same
     list isn't a new navigable state.

     Sequence on click: label → "Nalaganje ..." → new cards fade in → label
     settles back to "Naloži več" once the fade finishes, not the instant
     the response arrives — a small, deliberate beat rather than an abrupt
     swap. */
  loadMore(button) {
    const url = button.dataset.url;
    if (!url || button.disabled) return;

    const loadingText = button.dataset.labelLoading;

    button.disabled = true;
    button.classList.add('is-loading');
    if (loadingText) button.textContent = loadingText;

    fetch(this.sectionPageUrl(url), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const incomingGrid = doc.getElementById('AuraProductGrid');
        const currentGrid = document.getElementById('AuraProductGrid');
        if (incomingGrid && currentGrid) {
          Array.from(incomingGrid.children).forEach(item => {
            item.classList.add('is-revealing');
            currentGrid.appendChild(item);
          });
          /* A newly-appended page must respect an already-typed search term
             immediately, not flash 20-odd non-matching cards first. */
          this.applyCollectionSearch(this.searchTerm);
          requestAnimationFrame(() => {
            currentGrid.querySelectorAll('.is-revealing').forEach(item => {
              item.classList.remove('is-revealing');
            });
          });
        }

        const incomingLoadMore = doc.getElementById('AuraLoadMore');
        const currentLoadMore = document.getElementById('AuraLoadMore');

        /* Swap the button in AFTER the new cards' fade transition (.45s,
           see .aura-product-grid__item in aura-product-grid.css) finishes,
           so "button settles" reads as a consequence of the fade, not a
           simultaneous, unrelated flicker. */
        window.setTimeout(() => {
          if (currentLoadMore) {
            currentLoadMore.innerHTML = incomingLoadMore ? incomingLoadMore.innerHTML : '';
          }
          this.bindLoadMore();
        }, 480);
      })
      .catch(() => {
        button.disabled = false;
        button.classList.remove('is-loading');
        if (button.dataset.labelDefault) button.textContent = button.dataset.labelDefault;
      });
  }

  /* ----- PRICE SORT CORRECTION -------------------------------------------
     Shopify ranks price sorts by each product's CHEAPEST variant. AURA cards
     print product.price_max instead — the bottle, because the cheap variant is
     a sample — so Shopify's order and the visible prices disagree and
     "Cena: nižja → višja" reads as arbitrary. Shopify has no sort on max
     price, so the grid is re-ordered here against the price actually on the
     card.

     This needs the whole collection in the DOM first: ordered by MIN price,
     page 2 can hold a product whose bottle price belongs on page 1, so sorting
     only what's loaded would still be wrong. Same walk free-text search does,
     same 25-page cap.

     Runs on load and after every filter/sort swap; a no-op for every other
     sort, so nothing extra is fetched unless the shopper asked for price. */
  enforcePriceOrder() {
    const sort = this.sortInput ? this.sortInput.value : '';
    if (sort !== 'price-ascending' && sort !== 'price-descending') return;

    const grid = document.getElementById('AuraProductGrid');
    if (!grid || this.priceOrderRunning) return;

    const descending = sort === 'price-descending';

    /* Nothing left to fetch: reorder straight away, no loading state for what
       is a single synchronous pass. */
    if (!document.querySelector('[data-load-more]')) {
      this.sortGridByShownPrice(descending);
      return;
    }

    this.priceOrderRunning = true;
    /* Reuse the filter dim — the grid is about to reshuffle under the
       shopper, and it must not read as an interactive list meanwhile. The
       Load More button sits outside the grid, so disable it separately. */
    document.body.classList.add('is-filtering');
    document.querySelectorAll('[data-load-more]').forEach(btn => { btn.disabled = true; });

    this.loadRemainingPages()
      .catch(() => {})
      .then(() => {
        this.sortGridByShownPrice(descending);
        document.body.classList.remove('is-filtering');
        this.priceOrderRunning = false;
      });
  }

  /* Reorders the grid items in place by the price printed on the card. Stable
     for equal prices (Array.prototype.sort is stable), so products sharing a
     price keep Shopify's relative order rather than shuffling on every pass. */
  sortGridByShownPrice(descending) {
    const grid = document.getElementById('AuraProductGrid');
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll('.aura-product-grid__item'));
    if (items.length < 2) return;

    items
      .map(el => ({ el, price: auraCardPrice(el) }))
      .sort((a, b) => (descending ? b.price - a.price : a.price - b.price))
      .forEach(entry => grid.appendChild(entry.el));
  }

  /* A filter/sort AJAX swap replaces the accordion's inner markup wholesale
     (see AjaxTransport.swapFilterOptions), which resets the search input to
     empty and drops its bound listener — re-sync the value from this.searchTerm
     (the real source of truth) and rebind every time, not just once. */
  bindCollectionSearch() {
    document.querySelectorAll('[data-collection-search]').forEach(input => {
      input.value = this.searchTerm;
      const row = input.closest('.aura-filter-search-row');
      const resetBtn = row && row.querySelector('[data-collection-search-reset]');
      this.syncSearchReset(input, resetBtn);

      if (!input.dataset.auraBound) {
        input.dataset.auraBound = '1';
        input.addEventListener('input', () => this.syncSearchReset(input, resetBtn));
        input.addEventListener('input', debounce(() => {
          this.searchTerm = input.value.trim().toLowerCase();
          if (this.searchTerm) {
            this.searchEverything(this.searchTerm);
          } else {
            this.applyCollectionSearch('');
          }
        }, 150));
      }

      if (resetBtn && !resetBtn.dataset.auraBound) {
        resetBtn.dataset.auraBound = '1';
        resetBtn.addEventListener('click', () => {
          input.value = '';
          this.searchTerm = '';
          this.syncSearchReset(input, resetBtn);
          this.applyCollectionSearch('');
          input.focus();
        });
      }
    });
    this.applyCollectionSearch(this.searchTerm);
  }

  syncSearchReset(input, resetBtn) {
    if (resetBtn) resetBtn.hidden = !input.value;
  }

  setSearchLoading(isLoading) {
    document.querySelectorAll('.aura-filter-search').forEach(el => {
      el.classList.toggle('is-loading', isLoading);
    });
    document.querySelectorAll('[data-collection-search-status]').forEach(el => {
      el.hidden = !isLoading;
    });
  }

  /* A product outside the currently-paginated pages otherwise never gets a
     chance to match — silently walk the rest of "Load More" first (same
     endpoint the button uses, just without its click choreography), then
     filter once everything the collection has is actually in the DOM. */
  searchEverything(term) {
    if (!document.querySelector('[data-load-more]')) {
      this.applyCollectionSearch(term);
      return;
    }
    this.setSearchLoading(true);
    this.loadRemainingPages()
      .catch(() => {})
      .then(() => {
        this.setSearchLoading(false);
        /* Re-read this.searchTerm, not the `term` this call started with —
           a fast typist may have changed or cleared it while pages loaded. */
        this.applyCollectionSearch(this.searchTerm);
      });
  }

  /* Recursively fetches every remaining page (capped, so a very large
     catalog can't hang the browser) and appends straight into the grid — no
     reveal animation or label choreography here, this is a background
     catch-up, not the deliberate single click loadMore() handles.

     Two callers need the whole collection in the DOM: free-text search (a
     product on page 3 must still be findable) and the price-sort correction
     in enforcePriceOrder(). */
  loadRemainingPages(pagesLeft = 25) {
    const button = document.querySelector('[data-load-more]');
    if (!button || pagesLeft <= 0) return Promise.resolve();

    const url = button.dataset.url;
    if (!url) return Promise.resolve();

    return fetch(this.sectionPageUrl(url), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const incomingGrid = doc.getElementById('AuraProductGrid');
        const currentGrid = document.getElementById('AuraProductGrid');
        if (incomingGrid && currentGrid) {
          Array.from(incomingGrid.children).forEach(item => currentGrid.appendChild(item));
        }

        const incomingLoadMore = doc.getElementById('AuraLoadMore');
        const currentLoadMore = document.getElementById('AuraLoadMore');
        if (currentLoadMore) {
          currentLoadMore.innerHTML = incomingLoadMore ? incomingLoadMore.innerHTML : '';
        }
        this.bindLoadMore();

        return this.loadRemainingPages(pagesLeft - 1);
      });
  }

  /* Client-side only, by design — narrows what's already in the DOM by
     product title. Unlike every other filter here it never touches the URL
     or the server: Shopify's collection.filters has no free-text facet, so
     searchEverything() above walks the existing pagination first to make
     sure "what's in the DOM" really is the whole collection. */
  applyCollectionSearch(term) {
    const grid = document.getElementById('AuraProductGrid');
    if (!grid) return;

    let visibleCount = 0;
    grid.querySelectorAll('.aura-product-grid__item').forEach(item => {
      const titleEl = item.querySelector('.aura-card__title');
      const title = titleEl ? titleEl.textContent.trim().toLowerCase() : '';
      const matches = !term || title.includes(term);
      item.hidden = !matches;
      if (matches) visibleCount += 1;
    });

    let empty = grid.parentElement && grid.parentElement.querySelector('[data-search-empty]');
    if (term && visibleCount === 0) {
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'aura-collection-empty';
        empty.setAttribute('data-search-empty', '');
        empty.appendChild(document.createElement('p'));
        grid.insertAdjacentElement('afterend', empty);
      }
      empty.querySelector('p').textContent = `Ni izdelkov, ki bi ustrezali iskanju "${term}".`;
      empty.hidden = false;
    } else if (empty) {
      empty.hidden = true;
    }
  }
}

/* -------- Price range slider --------------------------------------------- */

class AuraPriceSlider {
  constructor(el, controller) {
    this.el = el;
    this.controller = controller;

    const rmin = parseFloat(el.dataset.rangeMin);
    const rmax = parseFloat(el.dataset.rangeMax);
    this.rangeMin = isNaN(rmin) ? 0 : rmin;
    /* Only fall back when the ceiling is genuinely missing/invalid — never let
       a real value (or a 0 from a bad data layer) silently become 100. */
    this.rangeMax = (isNaN(rmax) || rmax <= this.rangeMin) ? this.rangeMin + 100 : rmax;

    const span = (this.rangeMax - this.rangeMin) || 1;
    const vMin = parseFloat(el.dataset.valueMin);
    const vMax = parseFloat(el.dataset.valueMax);
    this.pMin = isNaN(vMin) ? 0 : (vMin - this.rangeMin) / span;
    this.pMax = isNaN(vMax) ? 1 : (vMax - this.rangeMin) / span;
    /* clamp to [0,1] — caps any out-of-range selection (e.g. a stale lte above
       the ceiling) at the slider's min/max. */
    this.pMin = Math.max(0, Math.min(1, this.pMin));
    this.pMax = Math.max(0, Math.min(1, this.pMax));

    this.paramMin = el.dataset.paramMin;
    this.paramMax = el.dataset.paramMax;
    this.currency = el.dataset.currency || '€';

    /* Minimum distance between thumbs, expressed in CURRENCY UNITS (1 unit)
       converted to a fraction of the current span. A fixed percentage would
       make the gap €10 on a 0–500 range but €40 on a 0–2000 range. */
    this.gap = 1 / span;

    this.track = el.querySelector('.aura-price-slider__track');
    this.fill = el.querySelector('.aura-price-slider__fill');
    this.thumbMin = el.querySelector('.aura-price-slider__thumb--min');
    this.thumbMax = el.querySelector('.aura-price-slider__thumb--max');
    this.inputMin = el.querySelector('.aura-price-slider__input--min');
    this.inputMax = el.querySelector('.aura-price-slider__input--max');

    this.active = null;

    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);

    this.render();
    this.bind();
  }

  val(p) {
    return Math.round(this.rangeMin + p * (this.rangeMax - this.rangeMin));
  }

  percent(clientX) {
    const r = this.track.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  }

  /* Write a value into an input without clobbering it while the shopper is
     typing in that field. Plain integers (no grouping) keep typing natural;
     the € lives in a separate suffix span. */
  setInput(input, value) {
    if (!input || document.activeElement === input) return;
    input.value = String(value);
  }

  render() {
    this.thumbMin.style.left = `${this.pMin * 100}%`;
    this.thumbMax.style.left = `${this.pMax * 100}%`;
    this.fill.style.left = `${this.pMin * 100}%`;
    this.fill.style.width = `${(this.pMax - this.pMin) * 100}%`;

    const minVal = this.val(this.pMin);
    const maxVal = this.val(this.pMax);

    this.setInput(this.inputMin, minVal);
    this.setInput(this.inputMax, maxVal);

    /* Keep ARIA in sync with the raw (unformatted) values as thumbs move. */
    this.thumbMin.setAttribute('aria-valuenow', minVal);
    this.thumbMax.setAttribute('aria-valuenow', maxVal);
  }

  onDown(thumb, e) {
    e.preventDefault();
    this.active = thumb;
    thumb.setPointerCapture(e.pointerId);
    thumb.addEventListener('pointermove', this.onMove);
    thumb.addEventListener('pointerup', this.onUp);
    thumb.addEventListener('pointercancel', this.onUp);
  }

  onMove(e) {
    if (!this.active) return;
    const p = this.percent(e.clientX);
    if (this.active === this.thumbMin) {
      this.pMin = Math.max(0, Math.min(p, this.pMax - this.gap));
    } else {
      this.pMax = Math.min(1, Math.max(p, this.pMin + this.gap));
    }
    this.render();
  }

  onUp(e) {
    if (!this.active) return;
    const thumb = this.active;
    thumb.removeEventListener('pointermove', this.onMove);
    thumb.removeEventListener('pointerup', this.onUp);
    thumb.removeEventListener('pointercancel', this.onUp);

    /* Release the capture taken in onDown — without this Safari can leave the
       thumb stuck in a dragging state after the gesture ends. */
    if (thumb.hasPointerCapture && thumb.hasPointerCapture(e.pointerId)) {
      thumb.releasePointerCapture(e.pointerId);
    }

    this.active = null;
    this.commit();
  }

  /* Click anywhere on the track moves the nearest thumb there and commits —
     matching Shopify's native price filter. Presses that start on a thumb are
     ignored here so they begin a drag instead. */
  onTrackDown(e) {
    if (e.target.closest('.aura-price-slider__thumb')) return;
    const p = this.percent(e.clientX);
    if (Math.abs(p - this.pMin) <= Math.abs(p - this.pMax)) {
      this.pMin = Math.max(0, Math.min(p, this.pMax - this.gap));
    } else {
      this.pMax = Math.min(1, Math.max(p, this.pMin + this.gap));
    }
    this.render();
    this.commit();
  }

  /* Translate thumb positions into gte/lte params and hand off to the
     transport. Uses raw integer major-unit values — no display formatting. */
  commit() {
    const minVal = this.val(this.pMin);
    const maxVal = this.val(this.pMax);
    const params = this.controller.currentParams();

    if (minVal > this.rangeMin) {
      params.set(this.paramMin, minVal);
    } else {
      params.delete(this.paramMin);
    }
    if (maxVal < this.rangeMax) {
      params.set(this.paramMax, maxVal);
    } else {
      params.delete(this.paramMax);
    }
    params.delete('page');

    sessionStorage.setItem('aura-filter-open', '1');
    this.controller.transport.apply(params);
  }

  /* Typed value → thumb position → commit. Digits-only parse makes it locale-
     proof (a Slovenian "1.299" reads as 1299, not 1.299) and matches the whole-
     euro scale; the value is clamped into range and past the other thumb. */
  onInputCommit(which) {
    const input = which === 'min' ? this.inputMin : this.inputMax;
    const digits = (input.value || '').replace(/[^\d]/g, '');
    if (digits === '') { this.render(); return; }

    let v = parseInt(digits, 10);
    v = Math.max(this.rangeMin, Math.min(this.rangeMax, v));
    const p = (v - this.rangeMin) / ((this.rangeMax - this.rangeMin) || 1);

    if (which === 'min') {
      this.pMin = Math.max(0, Math.min(p, this.pMax - this.gap));
    } else {
      this.pMax = Math.min(1, Math.max(p, this.pMin + this.gap));
    }

    this.render();
    this.commit();
  }

  bindInput(input, which) {
    if (!input) return;
    /* change fires on blur; Enter commits immediately (and is prevented from
       submitting the surrounding GET form). */
    input.addEventListener('change', () => this.onInputCommit(which));
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.onInputCommit(which);
        input.blur();
      }
    });
  }

  bind() {
    this.thumbMin.addEventListener('pointerdown', e => this.onDown(this.thumbMin, e));
    this.thumbMax.addEventListener('pointerdown', e => this.onDown(this.thumbMax, e));
    this.track.addEventListener('pointerdown', e => this.onTrackDown(e));
    this.bindInput(this.inputMin, 'min');
    this.bindInput(this.inputMax, 'max');
  }
}

/* -------- Active filter chips -------------------------------------------- */

class AuraActiveChips {
  constructor(controller) {
    this.controller = controller || null;
    this.render();
  }

  /* Route a chip's removal through the controller's AJAX path instead of
     letting the anchor navigate (which would hard-refresh the page). */
  bindRemoval(chip, item) {
    chip.addEventListener('click', e => {
      if (!this.controller) return;
      e.preventDefault();
      this.controller.removeFilter(item);
    });
  }

  collectActive() {
    const active = [];
    const seen = new Set();
    document.querySelectorAll(
      '.aura-overlay--filters .aura-filter-option.is-selected, .aura-filter-sidebar .aura-filter-option.is-selected'
    ).forEach(opt => {
      const key = (opt.dataset.param || '') + '|' + (opt.dataset.value || '');
      if (seen.has(key)) return;
      seen.add(key);

      const labelEl = opt.querySelector('.aura-filter-option__label');
      let label = opt.dataset.value || '';
      if (labelEl) {
        label = labelEl.textContent.trim();
      }
      active.push({ label, urlRemove: opt.dataset.urlRemove || '' });
    });
    return active;
  }

  /* A price filter (gte/lte) is active but never renders as an option chip, so
     detect it from the URL via the slider's own param names. This makes the
     reset control treat price as a real filter — so reset is reachable when
     only price is set, and clearing filters clears price too. */
  hasActivePrice() {
    const slider = document.querySelector('[data-price-slider]');
    if (!slider) return false;
    const params = new URLSearchParams(window.location.search);
    const pMin = slider.dataset.paramMin;
    const pMax = slider.dataset.paramMax;
    return Boolean((pMin && params.has(pMin)) || (pMax && params.has(pMax)));
  }

  buildBarChip(item) {
    const chip = document.createElement('a');
    chip.className = 'aura-chip';
    chip.href = item.urlRemove || '#';
    chip.innerHTML =
      `<span class="aura-chip__label">${item.label}</span>` +
      `<span class="aura-chip__remove" aria-hidden="true">&times;</span>`;
    chip.setAttribute('aria-label', `Odstrani filter: ${item.label}`);
    this.bindRemoval(chip, item);
    return chip;
  }

  buildFooterChip(item) {
    const chip = document.createElement('a');
    chip.className = 'aura-filter-footer__chip';
    chip.href = item.urlRemove || '#';
    chip.innerHTML =
      `${item.label}` +
      `<span class="aura-filter-footer__chip-remove" aria-hidden="true">&times;</span>`;
    chip.setAttribute('aria-label', `Odstrani filter: ${item.label}`);
    this.bindRemoval(chip, item);
    return chip;
  }

  render() {
    const active = this.collectActive();

    /* Filter bar chips (above the product grid) — re-query each render since
       AJAX may have replaced surrounding markup. */
    document.querySelectorAll('.aura-filter-bar__chips').forEach(container => {
      container.innerHTML = '';
      active.forEach(item => container.appendChild(this.buildBarChip(item)));
    });

    /* Overlay footer chips */
    const footerChips = document.querySelector('.aura-filter-footer__chips');
    if (footerChips) {
      footerChips.innerHTML = '';
      active.forEach(item => footerChips.appendChild(this.buildFooterChip(item)));
    }

    /* Any filter active = option chips OR an active price range. */
    const hasFilters = active.length > 0 || this.hasActivePrice();

    /* Show the footer bar ONLY when at least one filter is active. */
    const footerBar = document.querySelector('.aura-overlay--filters .aura-overlay__footer');
    if (footerBar) footerBar.hidden = !hasFilters;

    /* Reset buttons enabled whenever filters are active. */
    document.querySelectorAll('.aura-filter-footer__reset').forEach(btn => {
      btn.disabled = !hasFilters;
      if (btn.closest('.aura-filter-sidebar')) btn.hidden = !hasFilters;
    });
  }
}

/* -------- Floating bar scroll behaviour ---------------------------------- */

class AuraFloatingBar {
  constructor() {
    this.floatingBar = document.querySelector('.aura-filter-bar--floating');
    if (!this.floatingBar) return;
    this.lastScroll = window.scrollY;
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
  }
  handleScroll() {
    const y = window.scrollY;
    if (y < 150) { this.floatingBar.classList.remove('is-visible'); this.lastScroll = y; return; }
    if (y < this.lastScroll) this.floatingBar.classList.add('is-visible');
    else this.floatingBar.classList.remove('is-visible');
    this.lastScroll = y;
  }
}

/* -------- Boot ----------------------------------------------------------- */

document.addEventListener('DOMContentLoaded', () => {
  const form = document.querySelector('#AuraCollectionForm');
  if (form) new AuraCollectionController(form);

  new AuraFloatingBar();

  /* Re-open the filter overlay only after a HARD reload fallback (the AJAX
     path clears this flag because the overlay never closed). */
  if (sessionStorage.getItem('aura-filter-open') === '1') {
    sessionStorage.removeItem('aura-filter-open');
    const overlayEl = document.getElementById('filters');
    if (overlayEl) {
      overlayEl.classList.add('is-open');
      overlayEl.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      const panel = overlayEl.querySelector('.aura-overlay__panel');
      if (panel) requestAnimationFrame(() => panel.focus());
    }
  }
});
